import React, { useState } from 'react';
import { makeStyles, Popover, Button, FormHelperText } from '@material-ui/core';
import { TimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import ArrowDropDownOutlinedIcon from '@material-ui/icons/ArrowDropDownOutlined';
import moment from 'moment';
import { FieldProps, useField } from 'react-final-form';
import MomentUtils from '@date-io/moment';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
  field: {
    minWidth: 0,
  },
  input: {
    marginTop: theme.spacing(2),
  },
  popover: {
    '& .MuiToolbar-root': {
      display: 'none',
    },
  },
  dateButton: {
    border: 'none',
    textTransform: 'none',
    padding: '2px 2px 2px 8px',
    '&:hover': {
      border: 'none',
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

interface Props {
  label: string;
  name: string;
  validate?: FieldProps<any, any>['validate'];
  error?: boolean;
  errorDescriptionId?: string;
  ariaLabel?: string;
}

const TimeInputField = (props: Props) => {
  const { label, name, validate, error, errorDescriptionId, ariaLabel } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    input: { value, onChange },
    meta: { touched, error: validationError, submitError, submitFailed, dirtySinceLastSubmit },
  } = useField(name, { validate });

  const fieldError =
    (touched || submitFailed) && validationError
      ? validationError
      : submitFailed && !dirtySinceLastSubmit
        ? submitError
        : undefined;
  const fieldErrorId = `campaign-${name.replace(/\./g, '-')}-error`;
  const describedBy =
    [fieldError && fieldErrorId, errorDescriptionId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error || fieldError);

  const handleChange = (newValue) => {
    onChange(newValue ? newValue : null);
  };

  return (
    <>
      <Popover
        className={classes.popover}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <TimePicker
            className={classes.input}
            autoOk
            ampm
            value={value ? moment(value, 'HH:mm:ss') : null}
            onChange={handleChange}
            open={!!anchorEl}
            onClose={handleClose}
          />
        </MuiPickersUtilsProvider>
      </Popover>

      <div className={classes.field}>
        <Button
          onClick={handleOpen}
          color="primary"
          variant="outlined"
          aria-label={
            ariaLabel ? `${ariaLabel}${value ? `: ${value.format('LT')}` : ''}` : undefined
          }
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={classNames(classes.dateButton, invalid && classes.error)}>
          {value ? value.format('LT') : label}
          <ArrowDropDownOutlinedIcon />
        </Button>
        {fieldError && (
          <FormHelperText error id={fieldErrorId}>
            {fieldError}
          </FormHelperText>
        )}
      </div>
    </>
  );
};

export default TimeInputField;
